import { useCallback } from 'react'
import { createColumnHelper, type ColumnDef } from '@tanstack/react-table'
import type {
  BuildEventColumnsArgs,
  MatterAssetEventRow,
  MatterAssetFeatureConfig,
  MatterAssetOpenSource,
  MatterAssetReference,
  MatterAssetSourceRef,
} from '@/matter-asset-common/feature/matter-asset-feature-config'
import { useMatterAssetStoreKey } from '@/matter-asset-common/feature/matter-asset-feature-context'
import ReferencedTextCell from '@/matter-asset-common/events/ReferencedTextCell'
import AssetSourceFileCell from '@/matter-asset-common/events/AssetSourceFileCell'
import { UIMedChronEvent } from '@/medical-chronology/events/store/schemas'
import { selectUIMedChronEventsListByJobId } from '@/medical-chronology/events/store/selectors'
import ReferencedSummaryCell from '@/medical-chronology/events/components/ReferencedSummaryCell'
import { MedChronEventsTableVariant } from '@/medical-chronology/events/components/med-chron-events-table-variant'
import { selectMedChronReferenceById } from '@/medical-chronology/references/store/selectors'
import { useOpenMedChronSource } from '@/medical-chronology/sources/useOpenMedChronSource'
import type { MedChronSourceRef } from '@/medical-chronology/sources/schemas'
import type { MedChronReference } from '@/medical-chronology/references/store/schemas'
import { selectActiveContentViewerContextMatches } from '@/content-viewer/store/selectors'
import { ViewerOpenContextKind } from '@/content-viewer/store/schemas'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { selectDataErrorForAsset } from './store/selectors'
import { MedChronAssetActions } from './store/slice'

/**
 * Builds the medical-chronology events columns for the shared events table. Two display variants:
 * - All Events: base columns (columns marked `detailedViewOnly` are filtered out)
 * - Detailed View: every column
 *
 * The shared table appends the spacer column itself, so this returns only the data columns.
 */
function buildMedChronEventColumns({ handleScroll, variant }: BuildEventColumnsArgs): ColumnDef<MatterAssetEventRow>[] {
  const columnHelper = createColumnHelper<UIMedChronEvent>()

  // Define all columns once, then filter by variant below
  const allColumns = [
    columnHelper.display({
      id: 'rowNumber',
      header: '',
      // Show human-friendly row index
      cell: ({ row }) => <div className="p-1 pt-2 text-xs font-medium text-brand-neutral-600 flex justify-center">{row.index + 1}</div>,
      size: 35,
    }),
    columnHelper.accessor('dateOfService', {
      header: 'Date of Service',
      cell: ({ getValue, row, cell }) => <ReferencedTextCell item={getValue()} cellPosition={{ rowId: row.id, cellId: cell.id }} scrollToCell={handleScroll} />,
      size: 134,
      meta: { hoverable: true },
    }),
    columnHelper.accessor('facility', {
      header: 'Facility',
      cell: ({ getValue, row, cell }) => <ReferencedTextCell item={getValue()} cellPosition={{ rowId: row.id, cellId: cell.id }} scrollToCell={handleScroll} />,
      size: 172,
      meta: { hoverable: true },
    }),
    columnHelper.accessor('provider', {
      header: 'Provider',
      cell: ({ getValue, row, cell }) => <ReferencedTextCell item={getValue()} cellPosition={{ rowId: row.id, cellId: cell.id }} scrollToCell={handleScroll} />,
      size: 232,
      meta: { hoverable: true, detailedViewOnly: true },
    }),
    columnHelper.accessor('summarySentences', {
      header: 'Summary',
      cell: ({ getValue, row, cell }) => (
        <ReferencedSummaryCell items={getValue()} cellPosition={{ rowId: row.id, cellId: cell.id }} scrollToCell={handleScroll} />
      ),
      size: 578,
      meta: { hoverable: true },
    }),
    columnHelper.accessor('diagnoses', {
      header: 'Diagnoses',
      cell: ({ getValue, row, cell }) => (
        <ReferencedTextCell items={getValue()} cellPosition={{ rowId: row.id, cellId: cell.id }} scrollToCell={handleScroll} />
      ),
      size: 232,
      meta: { hoverable: true, detailedViewOnly: true },
    }),
    columnHelper.accessor('icd10Codes', {
      header: 'ICD-10 Codes',
      cell: ({ getValue, row, cell }) => (
        <ReferencedTextCell items={getValue()} cellPosition={{ rowId: row.id, cellId: cell.id }} scrollToCell={handleScroll} />
      ),
      size: 135,
      meta: { hoverable: true, detailedViewOnly: true },
    }),
    columnHelper.accessor('testsAndOrImaging', {
      header: 'Tests & Imaging',
      cell: ({ getValue, row, cell }) => (
        <ReferencedTextCell items={getValue()} cellPosition={{ rowId: row.id, cellId: cell.id }} scrollToCell={handleScroll} />
      ),
      size: 232,
      meta: { hoverable: true, detailedViewOnly: true },
    }),
    columnHelper.accessor('procedures', {
      header: 'Procedures',
      cell: ({ getValue, row, cell }) => (
        <ReferencedTextCell items={getValue()} cellPosition={{ rowId: row.id, cellId: cell.id }} scrollToCell={handleScroll} />
      ),
      size: 232,
      meta: { hoverable: true, detailedViewOnly: true },
    }),
    columnHelper.accessor('medications', {
      header: 'Medications',
      cell: ({ getValue, row, cell }) => (
        <ReferencedTextCell items={getValue()} cellPosition={{ rowId: row.id, cellId: cell.id }} scrollToCell={handleScroll} />
      ),
      size: 232,
      meta: { hoverable: true, detailedViewOnly: true },
    }),
    columnHelper.accessor('source', {
      header: 'Source File',
      cell: ({ getValue, row, cell }) => (
        <AssetSourceFileCell source={getValue()} pages={row.original.pages} cellPosition={{ rowId: row.id, cellId: cell.id }} scrollToCell={handleScroll} />
      ),
      size: 297,
    }),
  ]

  // Variant filter: In All Events, hide columns marked detailedViewOnly
  const filteredColumns = allColumns.filter((column) => {
    const detailedViewOnly = column.meta?.detailedViewOnly || false

    if (variant === MedChronEventsTableVariant.ALL_EVENTS) {
      return !detailedViewOnly // Show columns that are NOT detailed view only
    } else {
      return true // Show all columns in detailed view
    }
  })

  // The shared table works with a minimal row type; the columns above are typed against the full
  // UIMedChronEvent row they actually receive at runtime. Erase the row type at this boundary.
  return filteredColumns as unknown as ColumnDef<MatterAssetEventRow>[]
}

/**
 * Opens med-chron source files and references in the content viewer. Reference opens carry the
 * legacy reference + highlights and fire the reference-viewer-opened analytics; file-pill/page-chip
 * opens are file-level (no reference analytics). The shared cells narrow back through these.
 */
function useMedChronOpenSource(): MatterAssetOpenSource {
  const storeKey = useMatterAssetStoreKey()
  const { trackEvent } = useAnalytics()
  const { openMedChronSource, sourceLoadError } = useOpenMedChronSource()

  const openReferenceSource = useCallback(
    async (reference: MatterAssetReference) => {
      // The shared layer holds references as a minimal shape; here we work with the full one.
      const ref = reference as unknown as MedChronReference
      const opened = await openMedChronSource(ref.source, {
        legacyReference: ref,
        highlights: ref.highlights,
        startingPage: ref.highlights.length === 0 ? ref.pages?.[0] : undefined,
      })

      // Track reference viewer opened (only on a successful open)
      if (opened && storeKey) {
        trackEvent(AnalyticsEvent.MedChronAssetReferenceViewerOpened, {
          medChronAssetId: storeKey,
          medChronReferenceId: ref.id,
        })
      }

      return opened
    },
    [openMedChronSource, storeKey, trackEvent]
  )

  const openSourceFile = useCallback(
    (source: MatterAssetSourceRef, options?: { startingPage?: number }) => openMedChronSource(source as MedChronSourceRef, options),
    [openMedChronSource]
  )

  return { openReferenceSource, openSourceFile, sourceLoadError }
}

/**
 * Feature config that drives the shared matter-asset components for the living Medical Chronology.
 * Reproduces the med-chron data wiring, columns, copy, references, and retry behavior exactly.
 */
export const medChronFeatureConfig: MatterAssetFeatureConfig = {
  key: 'medchron',
  selectUIEventsByStoreKey: (state, { storeKey }) => selectUIMedChronEventsListByJobId(state, { medChronJobId: storeKey }),
  buildEventColumns: buildMedChronEventColumns,
  selectDataError: (state, { storeKey }) => selectDataErrorForAsset(state, { assetId: storeKey }),
  onRetryLoadEvents: (dispatch, { storeKey }) => dispatch(MedChronAssetActions.refreshAssetData({ assetId: storeKey })),
  copy: {
    eventsErrorTitle: 'Error Loading Medical Chronology',
    emptyEventsMessage: 'No events to display',
  },
  selectReferenceById: (state, { storeKey, referenceId }) => selectMedChronReferenceById(state, { medChronJobId: storeKey, referenceId }),
  selectIsReferenceActive: (state, { referenceId }) =>
    selectActiveContentViewerContextMatches(state, { kind: ViewerOpenContextKind.MED_CHRON_REFERENCE, referenceId }),
  useOpenSource: useMedChronOpenSource,
}
